import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";
import { useLocation } from "react-router-dom";
import { Mail, MapPin, Phone, Globe } from "lucide-react";

const Divider = () => (
  <hr className="border-t-2 border-[#FF4D00] max-w-6xl w-full mx-auto my-12 opacity-60" />
);

const ContactPage = () => {
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    inquiryType: "",
    message: "",
  });

  // Pre-fill form if coming from HomePage
  useEffect(() => {
    if (location.state) {
      setFormData((prev) => ({
        ...prev,
        name: location.state.name || "",
        email: location.state.email || "",
        inquiryType: location.state.inquiryType || "",
      }));
    }
  }, [location.state]);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}users/contact-form/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );
      
      if (response.ok) {
        alert("Your message has been sent!");
        setFormData({
          name: "",
          email: "",
          organization: "",
          inquiryType: "",
          message: "",
        });
      } else {
        const data = await response.json();
        alert("Error: " + (data.error || "Something went wrong."));
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An unexpected error occurred.");
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Helmet>
        <title>Contact Us | COBIT Governance Tool</title>
      </Helmet>

      {/* Contact Info Section */}
      <section className="py-20 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-4" data-aos="fade-down">
            Contact Us
          </h2>
          <p className="text-lg text-gray-600 mb-10" data-aos="fade-up">
            Have questions, feedback, or need assistance? Get in touch with us!
          </p>

          <div className="space-y-8 text-left" data-aos="fade-up" data-aos-delay="100">
            <div className="flex items-start gap-4">
              <MapPin className="text-[#FF4D00] w-6 h-6 mt-1" />
              <div>
                <h4 className="font-semibold text-lg">Address</h4>
                <p className="text-gray-700">
                  C105 THE SAILS, 14 TIMEBALL BOULEVARD, <br />
                  POINT WATERFRONT, DURBAN, 4000
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Phone className="text-[#FF4D00] w-6 h-6 mt-1" />
              <div>
                <h4 className="font-semibold text-lg">Phone</h4>
                <p className="text-gray-700">+27 (0)31 109 6662</p>
                <p className="text-gray-700">+27 (0)84 310 1221</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Mail className="text-[#FF4D00] w-6 h-6 mt-1" />
              <div>
                <h4 className="font-semibold text-lg">Email</h4>
                <a
                  href="mailto:info@izingcweti.co.za"
                  className="text-[#FF4D00] hover:underline"
                >
                  info@izingcweti.co.za
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Globe className="text-[#FF4D00] w-6 h-6 mt-1" />
              <div>
                <h4 className="font-semibold text-lg">Website</h4>
                <a
                  href="https://www.izingcweti.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF4D00] hover:underline"
                >
                  www.izingcweti.co.za
                </a>
                <p className="text-gray-700">Izingcweti ICT Solutions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="px-6 pb-20" data-aos="fade-up">
        <div className="max-w-3xl mx-auto bg-gray-50 shadow-md rounded-xl p-8">
          <h3 className="text-2xl font-bold mb-4 text-center">
            Need help, want to request access, or have a question?
          </h3>
          <p className="text-center text-gray-600 mb-8">
            Fill out the form below.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-medium mb-1">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#FF4D00]"
                required
              />
            </div>

            <div>
              <label className="block font-medium mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#FF4D00]"
                required
              />
            </div>

            <div>
              <label className="block font-medium mb-1">
                Organization (optional)
              </label>
              <input
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#FF4D00]"
              />
            </div>

            <div>
              <label className="block font-medium mb-1">Inquiry Type</label>
              <select
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#FF4D00]"
                required
              >
                <option value="">Select an option</option>
                <option value="Support">Support</option>
                <option value="Request Access">Request Access</option>
                <option value="Demo">Demo</option>
                <option value="Feedback">Feedback</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block font-medium mb-1">Message</label>
              <textarea
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#FF4D00]"
                required
              ></textarea>
            </div>

            <button
              type="submit"
              className="w-full bg-[#FF4D00] text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition"
            >
              Send Message
            </button>
          </form>
        </div>
      </section>

      <Divider />
    </div>
  );
};

export default ContactPage;
